import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { APP_TIMEZONE, formatApiDate } from '../utils/timezone';

const DashboardChatContext = createContext(null);

const getDefaultDateRangeThisMonth = () => {
  const now = moment().tz(APP_TIMEZONE);
  return {
    start: now.clone().startOf('month').toDate(),
    end: moment.min(now.clone().endOf('month'), now.clone()).endOf('day').toDate(),
  };
};

export function DashboardChatProvider({ children }) {
  const defaults = getDefaultDateRangeThisMonth();
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: defaults.start,
    end: defaults.end,
  });

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);

  const syncDateRange = useCallback((start, end) => {
    if (!start || !end) return;
    setDateRange({ start, end });
  }, []);

  const queryDefaults = useMemo(
    () => ({
      startDate: formatApiDate(dateRange.start),
      endDate: formatApiDate(dateRange.end),
      venue: 'both',
      metric: 'competition_status',
    }),
    [dateRange.end, dateRange.start]
  );

  const value = useMemo(
    () => ({
      isOpen,
      openChat,
      closeChat,
      toggleChat,
      dateRange,
      syncDateRange,
      queryDefaults,
    }),
    [closeChat, dateRange, isOpen, openChat, queryDefaults, syncDateRange, toggleChat]
  );

  return (
    <DashboardChatContext.Provider value={value}>
      {children}
    </DashboardChatContext.Provider>
  );
}

export function useDashboardChat() {
  const ctx = useContext(DashboardChatContext);
  if (!ctx) {
    throw new Error('useDashboardChat must be used within DashboardChatProvider');
  }
  return ctx;
}
